import React, { useState } from "react";
import { ReactComponent as GroupIcon } from '../../assets/admin/Group.svg';
import SearchBar from "../layout/SearchBar";
import {
  MynauiEditOne,
  TablerCaretUpDownFilled,
} from "../../assets/icons/icons";
import useSortableData from "../../utils/SortData";
import { Trash2 } from "lucide-react";
import EditUserDetails from "./EditUser";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmDialogBoxWithUser from "../layout/ConfirmDialogBoxWithUser";
import EmptyTableRecord from "../layout/EmptyTableRecord";
import { useSession } from "../layout/SessionContext";
import {deleteAdminUser} from "../../actions/ManageUserAction";

const TABLE_HEADERS = [
  { key: "uid", label: "ID" },
  { key: "name", label: "Name" },
  { key: "username", label: "Email address" },
  { key: "status", label: "Status", isStatus: true },
  { key: "productCount", label: "Action", isAction: true },
];

// Status style mapping
const getStatusStyle = (status) => {
  switch (status) {
    case "active":
      return "bg-[#D1FAE5] text-[#065F46]";
    case "inactive":
      return "bg-[#FEE2E2] text-[#991B1B]";
    default:
      return "bg-gray-100 text-gray-800";
  }
};


const USER_TABLE_HEADERS = [
  { key: "uid", label: "ID" },
  { key: "name", label: "Name" },
  { key: "username", label: "Email address" },
  { key: "status", label: "Status", isStatus: true },
  { key: "productCount", label: "Action", isAction: true },
];

const ManageUsersTable = ({ users, refreshData }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { hasPermission } = useSession();
  const [confirmEditDialog, setConfirmEditDialog] = useState(false);
  const [confirmDeleteDialogBox, setConfirmDeleteDialogBox] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showColumnPopup, setShowColumnPopup] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(USER_TABLE_HEADERS.map(h => h.key));
  const [tempColumns, setTempColumns] = useState(USER_TABLE_HEADERS.map(h => h.key));

  const { sortedItems: sortedUsers, handleSort } = useSortableData(users);

  const handleEditUser = (userId) => {
    const userToEdit = users.find((user) => user._id === userId);
    setSelectedUser(userToEdit);
    setConfirmEditDialog(true);
  };

  const handleConfirmDeleteDialogBox = (userId) => {
    const userToDelete = users.find((user) => user._id === userId);
    setSelectedUser(userToDelete);
  };

  const handleDeleteUser = async (userId) => {
    try {
      const adminId = localStorage.getItem("adminId");
      if (!adminId) {
        toast.error("Admin ID not found. Please log in again.");
        return;
      }
      const response = await deleteAdminUser(userId, adminId);
      if (response?.success) {
        toast.success(response.message || "User deleted successfully.");
        setConfirmDeleteDialogBox(false);
        refreshData();
      } else {
        toast.error(response?.message || "Failed to delete user.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  // Column selection logic
  const handleColumnToggle = (key) => {
    setTempColumns(cols =>
      cols.includes(key)
        ? cols.filter(col => col !== key)
        : [...cols, key]
    );
  };
  const handleResetColumns = () => {
    setTempColumns(USER_TABLE_HEADERS.map(h => h.key));
  };
  const handleApplyColumns = () => {
    setSelectedColumns(tempColumns);
    setShowColumnPopup(false);
  };

  return (
    <>
      {/* Mobile layout */}
      <div className="block md:hidden">
        <div className="flex flex-row items-center w-full gap-2 pt-8 mb-2">
          <div className="flex-1 flex items-center h-9">
            <SearchBar
              searchTerm={searchTerm}
              onChange={setSearchTerm}
              onSearch={() => {}}
              className="w-full"
            />
          </div>
          <button
            className="flex items-center justify-center h-9 p-0"
            aria-label="Group"
            style={{ minWidth: 40, borderRadius: '6px', border: '1px solid #E5E7EB', background: '#FFF', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}
            onClick={() => setShowColumnPopup(true)}
          >
            <GroupIcon className="w-6 h-6 text-gray-500" />
          </button>
          {showColumnPopup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" onClick={() => setShowColumnPopup(false)}>
              <div className="bg-white rounded-lg p-6 w-80 max-w-sm shadow-lg" onClick={e => e.stopPropagation()}>
                <h2 className="text-lg font-semibold mb-4">Edit columns</h2>
                <div className="flex flex-col mb-4">
                  {USER_TABLE_HEADERS.map((header, idx) => (
                    <React.Fragment key={header.key}>
                      <label className="flex items-center gap-2 text-base py-2">
                        <input
                          type="checkbox"
                          checked={tempColumns.includes(header.key)}
                          onChange={() => handleColumnToggle(header.key)}
                          className="accent-primary w-4 h-4"
                        />
                        <span>{header.label} <span className="text-xs text-gray-400"></span></span>
                      </label>
                      {idx < USER_TABLE_HEADERS.length - 1 && (
                        <hr className="border-t border-gray-200" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="mt-6 flex justify-between gap-2">
                  <div className="flex justify-end gap-2 mt-2 w-full">
                    <button
                      onClick={handleResetColumns}
                      className="border border-gray-300 rounded-md px-4 py-2 text-sm font-medium bg-white text-gray-700"
                    >
                      Reset
                    </button>
                    <button
                      onClick={handleApplyColumns}
                      style={{ backgroundColor: '#22C55E' }}
                      className="text-white rounded-md px-4 py-2 text-sm font-medium"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="rounded-lg border-t border-x border-b border-gray-300 overflow-x-auto mt-4">
          <div className="flex flex-col">
            {users && users.length > 0 ? (
              users.map((user) => (
                <table key={user._id} className="w-full border border-gray-200 bg-white shadow-sm">
                  <tbody>
                    {USER_TABLE_HEADERS.filter(h => selectedColumns.includes(h.key)).map(header => {
                      if (header.isAction) {
                        return (
                          <tr key={header.key}>
                            <td className="py-2 px-2 text-[16px] font-medium   font-rubik text-gray-900 w-1/2 align-top">
                              {header.label}
                            </td>
                            <td className="py-2 px-2 text-[16px] font-normal font-rubik text-gray-700 w-1/2 align-top break-words max-w-[140px] overflow-hidden text-ellipsis">
                              <div className="flex justify-start gap-2 min-h-6">
                                <button
                                  onClick={() => handleEditUser(user._id)}
                                  className="p-2 rounded bg-gray-100"
                                >
                                  <MynauiEditOne className="text-gray-400" />
                                </button>
                                <button
                                  onClick={() => {
                                    handleConfirmDeleteDialogBox(user._id);
                                    setConfirmDeleteDialogBox(true);
                                  }}
                                  className="p-2 rounded bg-gray-100"
                                >
                                  <Trash2 className="text-gray-400 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      if (header.isStatus) {
                        return (
                          <tr key={header.key}>
                            <td className="py-2 px-2 text-[16px] font-medium font-rubik text-gray-900 w-1/2 align-top">
                              {header.label}
                            </td>
                            <td className="py-2 px-2 text-[16px] font-normal font-rubik text-gray-700 w-1/2 align-top break-words max-w-[140px] overflow-hidden text-ellipsis">
                              <span
                                className={`px-3 py-1 text-[12px] rounded-full font-medium ${getStatusStyle(user.status)}`}
                              >
                                {user.status ? user.status.toUpperCase() : ""}
                              </span>
                            </td>
                          </tr>
                        );
                      }
                      if (header.key === "username") {
                        return (
                          <tr key={header.key}>
                            <td className="py-2 px-2 text-[16px] font-medium font-rubik text-gray-900 w-1/2 align-top">
                              {header.label}
                            </td>
                            <td className="py-2 px-2 text-[16px] font-normal font-rubik text-gray-700 w-1/2 align-top break-words max-w-[140px] overflow-hidden text-ellipsis">
                              {user.username ? user.username : "N/A"}
                            </td>
                          </tr>
                        );
                      }
                      return (
                        <tr key={header.key}>
                          <td className="py-2 px-2 text-[16px] font-medium  text-gray-900 w-1/2 align-top">{header.label}</td>
                          <td className="py-2 px-2 text-[16px] font-normal  text-gray-700 w-1/2 align-top break-words max-w-[140px] overflow-hidden text-ellipsis">{user[header.key] || "N/A"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ))
            ) : (
              <div className="text-center text-gray-500 p-4 bg-white border border-gray-200">No users found</div>
            )}
          </div>
        </div>
        {/* Mobile Pagination removed, should be handled by parent or only rendered once */}
        {confirmDeleteDialogBox && (
          <ConfirmDialogBoxWithUser
            handleCancel={() => setConfirmDeleteDialogBox(false)}
            message="Are you sure you want to <strong> delete this user?</strong> This action is permanent and cannot be undone. Once deleted, this user will loose the access to the portal."
            type="inactive"
            handleConfirm={() => handleDeleteUser(selectedUser._id)}
            confirmMessage="Confirm & Delete"
            hideUsernameInput={true}
          />
        )}
        {confirmEditDialog && (
          <EditUserDetails
            closeModal={() => setConfirmEditDialog(false)}
            user={selectedUser}
            refreshData={refreshData}
          />
        )}
      </div>
      {/* Desktop layout */}
      <div className="hidden md:block rounded-lg border-t border-x border-b border-gray-300 overflow-x-auto mb-6">
        <table className="w-full  border-gray-300 rounded-md">
          <thead className="bg-gray-100 border-b ">
            <tr className="md:h-10">
              {TABLE_HEADERS.map((header, index) => {
                  if (header.isAction && !hasPermission("Manage users", "edit")) {
                  return null;
                }
                return(
                  <th
                    key={index}
                    className="py-3 text-center font-semibold text-[15px] text-text1 whitespace-nowrap px-2 md:h-10"
                  >
                  {!header.isAction ? (
                    <div
                      className="flex items-center cursor-pointer text-left"
                      onClick={() => handleSort(header.key)}
                    >
                      <TablerCaretUpDownFilled className="mr-2" />
                      {header.label}
                    </div>
                  ) : (
                    <div className="flex text-left items-center">
                      {header.label}
                    </div>
                  )}
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {sortedUsers.length > 0 ? (
              sortedUsers.map((user) => (
                <tr key={user._id} className="hover:bg-gray-50 md:h-[60px]">
                  {TABLE_HEADERS.map((header) => {
                      if (header.isAction && !hasPermission("Manage users", "edit")) {
                  return null;
                }
                    return(
                    <td
                      key={header.key}
                        className="py-4 pl-2 text-[15px] font-normal text-[#374151] text-left md:h-[60px]"
                      >
                      {header.isAction ? (
                        <div className="flex justify-start min-h-6">
                            <>
                              <div className="flex justify-center">
                                <button
                                  onClick={() => handleEditUser(user._id)}
                                  className="px-2 rounded"
                                >
                                  <MynauiEditOne className="text-gray-400" />
                                </button>
                              </div>
                              <div className="flex justify-center">
                                <button
                                  onClick={() => {
                                    handleConfirmDeleteDialogBox(user._id);
                                    setConfirmDeleteDialogBox(true);
                                  }}
                                  className="px-2 rounded"
                                >
                                  <Trash2 className="text-gray-400 h-5" />
                                </button>
                              </div>
                            </>
                        </div>
                      ) : header.isStatus ? (
                        <span
                          className={`px-3 py-1 rounded-full text-[12px] font-medium ${getStatusStyle(
                            user.status
                          )}`}
                        >
                         {user.status ? user.status.toUpperCase() : ""}
                        </span>
                      ) : header.key === "username" ? (
                        user.username ? (
                          user.username
                        ) : (
                          "N/A"
                        )
                      ) : (
                        user[header.key] || "N/A"
                      )}
                    </td>
                  );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={TABLE_HEADERS.length}
                  className=" text-center text-gray-500"
                >
                  <EmptyTableRecord />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {confirmDeleteDialogBox && (
          <ConfirmDialogBoxWithUser
            handleCancel={() => setConfirmDeleteDialogBox(false)}
            message="Are you sure you want to <strong> delete this user?</strong> This action is permanent and cannot be undone. Once deleted, this user will loose the access to the portal."
            type="inactive"
            handleConfirm={() => handleDeleteUser(selectedUser._id)}
            confirmMessage="Confirm & Delete"
            hideUsernameInput={true}
          />
        )}
        {confirmEditDialog && (
          <EditUserDetails
            closeModal={() => setConfirmEditDialog(false)}
            user={selectedUser}
            refreshData={refreshData}
          />
        )}
      </div>
    </>
  );
};

export default ManageUsersTable;
